import { Link } from "react-router-dom";
import { useSeo } from "@/lib/useSeo";
import "@/pages/agent-team.css";

const BOOK_URL = "https://calendar.app.google/MFyPpbRo7HG92Q1YA";
const MYANSWERWIKI_URL = "https://myanswerwiki.com/";

export default function AgentTeam() {
  useSeo({
    title: "Your Agent Team | AI Marketing Agents by Craig Maloney",
    description:
      "See how Craig Maloney's 96 AI agents across six teams, led by one orchestrator, optimize your customer acquisition cost with built-in governance and a Board of Directors.",
    canonical: "https://craigmaloney.com/agent-team",
    og: {
      title: "Your Agent Team | Craig Maloney",
      description:
        "One orchestrator. Six distinct teams. 96 agents, a Board of Directors, and a critic called The Burned Hand keeping every plan honest.",
      image: "https://craigmaloney.com/og-agent-team.png",
      type: "website",
      url: "https://craigmaloney.com/agent-team",
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "AI marketing agent deployment",
      provider: {
        "@type": "Person",
        name: "Craig Maloney",
        jobTitle: "AI Growth Consultant",
      },
      description:
        "A 96-agent marketing team across six departments, led by one orchestrator, governed by a Board of Directors and a critic role called The Burned Hand.",
    },
  });

  return (
    <div className="at-page">
      <nav>
        <div className="wrap">
          <div className="logo">
            <Link to="/">Craig Maloney</Link>
          </div>
          <div className="navlinks">
            <Link className="active" to="/agent-team">
              Your agent team
            </Link>
            <a className="muted" href={MYANSWERWIKI_URL} target="_blank" rel="noopener noreferrer">
              MyAnswerWiki
            </a>
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="btn-blue" data-testid="nav-book-btn">
              Book a call
            </a>
          </div>
        </div>
      </nav>

      <header className="hero">
        <div className="wrap">
          <h1>Your Agent Team</h1>
          <p className="sub1">One orchestrator. Five distinct teams.</p>
        </div>
      </header>

      <section className="teamrow">
        <div className="wrap">
          <div className="smallgrid">
            <div className="smallbox sb1">
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>
              <div className="name">Visibility</div>
            </div>
            <div className="smallbox sb2">
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v11H8l-4 4V4z" /></svg>
              <div className="name">Engagement</div>
            </div>
            <div className="smallbox sb3">
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5.5" /><circle cx="12" cy="12" r="2" /></svg>
              <div className="name">Conversion</div>
            </div>
            <div className="smallbox sb4">
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 7-8" /><path d="M15 6h5v5" /></svg>
              <div className="name">Sales enablement</div>
            </div>
            <div className="smallbox sb5">
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 11a8 8 0 1 0-2.6 6.4" /><path d="M20 5v6h-6" /></svg>
              <div className="name">Retention</div>
            </div>
          </div>
        </div>
      </section>

      <section className="specialty">
        <div className="wrap">
          <div className="specialty-card">
            <div className="specialty-header">
              <h2>Built to hit Your Target Customer Acquisition Cost</h2>
              <div className="cac-target">
                <svg aria-hidden="true" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="46" fill="#D92D20" />
                  <circle cx="50" cy="50" r="33" fill="#fff" />
                  <circle cx="50" cy="50" r="20" fill="#D92D20" />
                  <circle cx="50" cy="50" r="7" fill="#fff" />
                </svg>
              </div>
            </div>
            <div className="specialty-punch">
              <p>You tell us where you want to go.</p>
              <p className="dim">We show you exactly how we&apos;ll get there, every month.</p>
            </div>
            <div className="cacchart">
              <div className="chartlabel">CAC glide path, 12 months</div>
              <svg aria-hidden="true" viewBox="0 0 640 180" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="cacFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3457D5" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#3457D5" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <line x1="0" y1="20" x2="640" y2="20" stroke="rgba(255,255,255,0.08)" />
                <line x1="0" y1="55" x2="640" y2="55" stroke="rgba(255,255,255,0.08)" />
                <line x1="0" y1="90" x2="640" y2="90" stroke="rgba(255,255,255,0.08)" />
                <line x1="0" y1="125" x2="640" y2="125" stroke="rgba(255,255,255,0.08)" />
                <line x1="0" y1="145" x2="640" y2="145" strokeDasharray="4 5" stroke="#F79009" strokeWidth="1.5" />
                <polygon points="10,25 220,60 420,105 630,140 630,145 10,145" fill="url(#cacFill)" />
                <polyline points="10,25 220,60 420,105 630,140" fill="none" stroke="#5B8DEF" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="10" cy="25" r="6" fill="#E2622A" />
                <circle cx="220" cy="60" r="4" fill="#5B8DEF" />
                <circle cx="420" cy="105" r="4" fill="#5B8DEF" />
                <circle cx="630" cy="140" r="6" fill="#0E9F6E" />
                <text x="4" y="14" fill="#c7cedb" fontSize="12" fontFamily="Inter,sans-serif">Today</text>
                <text x="560" y="165" fill="#F79009" fontSize="12" fontFamily="Inter,sans-serif">Target CAC</text>
                <text x="0" y="176" fill="#8b93a3" fontSize="11" fontFamily="Inter,sans-serif">Month 0</text>
                <text x="205" y="176" fill="#8b93a3" fontSize="11" fontFamily="Inter,sans-serif">Month 4</text>
                <text x="405" y="176" fill="#8b93a3" fontSize="11" fontFamily="Inter,sans-serif">Month 8</text>
                <text x="600" y="176" fill="#8b93a3" fontSize="11" fontFamily="Inter,sans-serif">Month 12</text>
              </svg>
            </div>
          </div>
        </div>
      </section>

      <section className="detail">
        <div className="wrap">
          <div className="detail-head">
            <h2>What each team delivers</h2>
            <div className="agentcount">96 agents across six teams</div>
          </div>
          <div className="detailgrid">
            <div className="detailcard dc1">
              <div className="dc-head">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>
                <h3>Visibility</h3>
              </div>
              <div className="dc-cols">
                <div className="col-left">
                  <div className="collabel">You get</div>
                  <ul><li>Shows up first</li><li>Cited by AI search</li></ul>
                </div>
                <div className="col-right">
                  <div className="collabel">Tactics</div>
                  <ul><li>SEO content writer</li><li>Local SEO, GMB and citations</li><li>AEO agent for AI search</li></ul>
                </div>
              </div>
            </div>
            <div className="detailcard dc2">
              <div className="dc-head">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v11H8l-4 4V4z" /></svg>
                <h3>Engagement</h3>
              </div>
              <div className="dc-cols">
                <div className="col-left">
                  <div className="collabel">You get</div>
                  <ul><li>An audience that stays warm</li><li>Content that keeps showing up</li></ul>
                </div>
                <div className="col-right">
                  <div className="collabel">Tactics</div>
                  <ul><li>Social agents, sentiment and content</li><li>Content posting agent</li><li>Graphic designer for assets</li></ul>
                </div>
              </div>
            </div>
            <div className="detailcard dc3">
              <div className="dc-head">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5.5" /><circle cx="12" cy="12" r="2" /></svg>
                <h3>Conversion</h3>
              </div>
              <div className="dc-cols">
                <div className="col-left">
                  <div className="collabel">You get</div>
                  <ul><li>Ad spend that turns into customers</li><li>What doesn&apos;t convert gets cut</li></ul>
                </div>
                <div className="col-right">
                  <div className="collabel">Tactics</div>
                  <ul><li>Media buyer, Meta and TikTok</li><li>Campaign builder</li><li>Analytics and attribution</li></ul>
                </div>
              </div>
            </div>
            <div className="detailcard dc4">
              <div className="dc-head">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 7-8" /><path d="M15 6h5v5" /></svg>
                <h3>Sales enablement</h3>
              </div>
              <div className="dc-cols">
                <div className="col-left">
                  <div className="collabel">You get</div>
                  <ul><li>A pipeline that stays full</li><li>New opportunities every week</li></ul>
                </div>
                <div className="col-right">
                  <div className="collabel">Tactics</div>
                  <ul><li>Strategist, channel mix and planning</li><li>Community agent for outreach</li><li>Prospect identification</li></ul>
                </div>
              </div>
            </div>
            <div className="detailcard dc5">
              <div className="dc-head">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 11a8 8 0 1 0-2.6 6.4" /><path d="M20 5v6h-6" /></svg>
                <h3>Retention</h3>
              </div>
              <div className="dc-cols">
                <div className="col-left">
                  <div className="collabel">You get</div>
                  <ul><li>Customers who stick around</li><li>Nothing falls through after the sale</li></ul>
                </div>
                <div className="col-right">
                  <div className="collabel">Tactics</div>
                  <ul><li>CRM layer, GoHighLevel backbone</li></ul>
                </div>
              </div>
            </div>
            <div className="detailcard dc6">
              <div className="dc-head">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                <h3>Operations</h3>
              </div>
              <div className="dc-cols">
                <div className="col-left">
                  <div className="collabel">You get</div>
                  <ul><li>Day to day runs smoothly</li><li>Problems get flagged early</li></ul>
                </div>
                <div className="col-right">
                  <div className="collabel">Tactics</div>
                  <ul><li>Ops agents for daily checklists</li><li>Vendor alerts</li></ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="governance">
        <div className="wrap">
          <div className="kicker">How it&apos;s governed</div>
          <h2>The Orchestrator leads the Captain, the Architect, and the Data Scientist</h2>
          <div className="orgchart">
            <div className="org-node org-top">
              <div className="role">Orchestrator</div>
              <div className="desc">Built from your LLMs.txt file. Leads the three roles below and routes work to all five teams.</div>
            </div>
            <div className="org-stem"></div>
            <div className="org-row">
              <div className="org-node">
                <div className="role">Captain</div>
                <div className="desc">Sets the strategy and direction the whole system works toward.</div>
              </div>
              <div className="org-node">
                <div className="role">Architect</div>
                <div className="desc">Checks feasibility and pricing before any plan moves forward.</div>
              </div>
              <div className="org-node">
                <div className="role">Data Scientist</div>
                <div className="desc">Tracks what&apos;s working and feeds the numbers back up the chain.</div>
              </div>
            </div>
            <div className="org-stem"></div>
            <div className="org-node org-bottom">
              <div className="role">The Burned Hand</div>
              <div className="desc">Makes sure their work will actually succeed. Stress-tests the plan before it moves any further.</div>
            </div>
          </div>
        </div>
      </section>

      <section className="board">
        <div className="wrap">
          <div className="kicker">Board of Directors</div>
          <h2>Every strategy comes with a second opinion</h2>
          <p className="note">Not to slow the team down. To make sure you&apos;re looking at the plan from every angle before you commit.</p>
          <div className="boardflow">
            <div className="bnode n1">
              <div className="badge"><svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.9 6.6 7.1.6-5.4 4.7 1.6 7-6.2-3.8-6.2 3.8 1.6-7L1.9 9.2l7.1-.6z" /></svg></div>
              <h3>Orchestrator proposes</h3>
              <ul>
                <li>Built against your CAC goal</li>
                <li>Mapped to the 12 month glide path</li>
              </ul>
            </div>
            <div className="barrow"><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg></div>
            <div className="bnode n2">
              <div className="badge"><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18" /><path d="M4 21V9l8-5 8 5v12" /><path d="M9 21v-6h6v6" /></svg></div>
              <h3>Board of Directors weighs in</h3>
              <div className="modechips">
                <span><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>Help me make this work</span>
                <span><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>Tell me why this won&apos;t</span>
              </div>
              <ul><li>Yours to set, add your own voices</li></ul>
            </div>
            <div className="barrow"><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg></div>
            <div className="bnode n3">
              <div className="badge"><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M9 9.5s1 1.2 3 1.2 3-1.2 3-1.2" /><path d="M4 21c0-4 3.5-7 8-7s8 3 8 7" /></svg></div>
              <h3>You decide</h3>
              <ul>
                <li>Strategy in one hand</li>
                <li>Second opinion in the other</li>
                <li>You make the call</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="closer">
        <div className="wrap">
          <h2>Grow your business today. Get started now.</h2>
          <div className="ctas">
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="btn-blue" data-testid="closer-book-btn">
              Book a call
            </a>
          </div>
        </div>
      </section>

      <footer>
        <div className="wrap">
          <div className="foot-top">
            <div className="foot-brand">
              <h3>AI Growth Consultant</h3>
              <p>A closed loop marketing system for modern marketing, built once and owned by you.</p>
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="btn-blue">Book a call</a>
            </div>
            <div className="foot-cols">
              <div className="foot-col">
                <h4>The system</h4>
                <Link to="/assessment">Free AI Marketing Assessment</Link>
                <a href={MYANSWERWIKI_URL} target="_blank" rel="noopener noreferrer">MyAnswerWiki</a>
                <Link to="/strategicroi">StrategicROI</Link>
                <a href="#">Citation Database</a>
                <a href="https://gauntlet.craigmaloney.com" target="_blank" rel="noopener noreferrer">The Gauntlet Market Assessment Tool</a>
              </div>
              <div className="foot-col">
                <h4>Resources</h4>
                <a href="/llms.txt" target="_blank" rel="noopener noreferrer">The LLMs.txt File</a>
              </div>
              <div className="foot-col">
                <h4>Legal</h4>
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/terms">Terms and Conditions</Link>
              </div>
            </div>
          </div>
          <div className="foot-bottom">
            <div>© 2026 Craig Maloney. All rights reserved.</div>
            <div className="legal"><Link to="/privacy">Privacy Policy</Link><Link to="/terms">Terms and Conditions</Link></div>
          </div>
        </div>
      </footer>
    </div>
  );
}
